// src/components/financials/LiabilityManager.js
// Created: 01/14/2026

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, TextField, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  IconButton, Card, CardContent 
} from '@mui/material';
import { AddCircle, Delete, AccountBalanceWallet } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const LiabilityManager = () => {
  const { userProfile } = useAuth();
  const [liabilities, setLiabilities] = useState([]);
  const [newLiability, setNewLiability] = useState({ name: '', amount: '', type: 'Mortgage' });
  const [loading, setLoading] = useState(false);

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (churchId) fetchLiabilities();
  }, [churchId]);

  const fetchLiabilities = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'liabilities'));
    setLiabilities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleAdd = async () => {
    if (!newLiability.name || !newLiability.amount) return;
    setLoading(true);
    try {
      await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'liabilities'), {
        ...newLiability,
        amount: parseFloat(newLiability.amount),
        createdAt: serverTimestamp()
      });
      setNewLiability({ name: '', amount: '', type: 'Mortgage' });
      fetchLiabilities();
    } catch (e) {
      alert(e.message);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this liability record?")) return;
    await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'liabilities', id));
    fetchLiabilities();
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceWallet sx={{ mr: 1 }} /> Liabilities & Debt
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Enter mortgages, loans, or other long-term debts.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Lender/Name" size="small" value={newLiability.name} onChange={(e) => setNewLiability({...newLiability, name: e.target.value})} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField fullWidth label="Amount Owed" size="small" type="number" value={newLiability.amount} onChange={(e) => setNewLiability({...newLiability, amount: e.target.value})} />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField select fullWidth label="Type" size="small" SelectProps={{ native: true }} value={newLiability.type} onChange={(e) => setNewLiability({...newLiability, type: e.target.value})}>
              <option value="Mortgage">Mortgage</option>
              <option value="Loan">Loan</option>
              <option value="Credit Line">Credit Line</option>
              <option value="Other">Other</option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="contained" startIcon={<AddCircle />} onClick={handleAdd} disabled={loading}>Add</Button>
          </Grid>
        </Grid>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liabilities.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.name}</TableCell>
                <TableCell>{l.type}</TableCell>
                <TableCell align="right">${(l.amount || 0).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => handleDelete(l.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {liabilities.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">No debts recorded.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default LiabilityManager;